/**
 * CenterPreview — dual video tiles (input/output) + mode toggle + toolbar.
 */

import type { FaceProfile, SwapMode } from "@/components/tabs/FaceSwapTab";
import type { SwapStatus as LiveKitStatus } from "@/hooks/useFaceSwapLiveKit";
import type { CameraDevice } from "@/hooks/useCameraDevices";
import { isLiteEdition } from "@/lib/config";

export function CenterPreview({
  videoRef, outputRef, processedVideoRef,
  hasStream, swapping, fps, frames,
  canSwap, activeProfile,
  swapMode, lkStatus, lkError,
  devices = [], selectedDeviceId = "", onSelectCamera,
  vcamActive = false, onToggleVCam,
  vmicActive = false, onToggleVMic,
  onTogglePiP, isPiPActive = false,
  onSwapModeChange,
  onStartWebcam, onStopWebcam, onStartSwap, onStopSwap,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
  outputRef: React.RefObject<HTMLImageElement>;
  processedVideoRef: React.RefObject<HTMLVideoElement>;
  hasStream: boolean; swapping: boolean;
  fps: number; frames: number;
  canSwap: boolean;
  activeProfile: FaceProfile | undefined;
  swapMode: SwapMode;
  lkStatus: LiveKitStatus;
  lkError: string | null;
  devices?: CameraDevice[];
  selectedDeviceId?: string;
  onSelectCamera: (deviceId: string) => void;
  vcamActive?: boolean;
  onToggleVCam: () => void;
  vmicActive?: boolean;
  onToggleVMic: () => void;
  onTogglePiP?: () => void;
  isPiPActive?: boolean;
  onSwapModeChange: (mode: SwapMode) => void;
  onStartWebcam: () => void; onStopWebcam: () => void;
  onStartSwap: () => void; onStopSwap: () => void;
}) {
  const isLiveKit = swapMode === "livekit";
  const outputLabel = isLiveKit
    ? (swapping ? `WebRTC · ${fps}fps` : "WebRTC Idle")
    : (swapping ? `Shared Memory · ${fps}fps` : "Passthrough");

  return (
    <div style={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      padding: "var(--space-5)",
      gap: "var(--space-4)",
    }}>
      {/* Mode toggle & device toolbar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-2)",
        flexWrap: "wrap",
        flexShrink: 0,
      }}>
        <ModeButton
          label="WebRTC (Cloud)"
          active={swapMode === "livekit"}
          onClick={() => onSwapModeChange("livekit")}
        />
        {!isLiteEdition() && (
          <ModeButton
            label="Local (Shared Memory)"
            active={swapMode === "local"}
            onClick={() => onSwapModeChange("local")}
          />
        )}

        <div style={{ width: 1, height: 18, background: "var(--border-subtle)", margin: "0 2px" }} />

        {/* Camera Selection Dropdown */}
        <select
          value={selectedDeviceId}
          onChange={(e) => onSelectCamera(e.target.value)}
          style={{
            height: 28,
            padding: "0 var(--space-2)",
            borderRadius: "var(--radius-md)",
            background: "var(--bg-floating)",
            border: "1px solid var(--border-default)",
            color: "var(--text-primary)",
            fontSize: "var(--text-xs)",
            cursor: "pointer",
            fontFamily: "var(--font-sans)",
            maxWidth: 180,
          }}
        >
          {devices.length === 0 ? (
            <option value="">No Camera Found</option>
          ) : (
            devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label} {d.isVirtual ? "(Virtual)" : ""}
              </option>
            ))
          )}
        </select>

        {/* Projector / Virtual Camera Button */}
        <button
          onClick={onToggleVCam}
          title={isLiteEdition() ? "Launch Clean Studio Projector for OBS Window Capture" : "Toggle Virtual Camera"}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-1)",
            height: 28,
            padding: "0 var(--space-3)",
            borderRadius: "var(--radius-md)",
            border: `1px solid ${vcamActive ? "var(--status-live)" : "var(--border-default)"}`,
            background: vcamActive ? "var(--status-live-dim)" : "var(--bg-floating)",
            color: vcamActive ? "var(--status-live)" : "var(--text-secondary)",
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-semibold)",
            fontFamily: "var(--font-sans)",
            cursor: "pointer",
            transition: "all var(--duration-fast)",
          }}
        >
          <span>{isLiteEdition() ? "OBS Projector" : "Virtual Cam"}</span>
          <span
            style={{
              fontSize: "9px",
              padding: "1px 5px",
              borderRadius: "var(--radius-full)",
              background: vcamActive ? "var(--status-live)" : "var(--bg-elevated)",
              color: vcamActive ? "#000" : "var(--text-muted)",
              fontWeight: "var(--weight-bold)",
              textTransform: "uppercase",
            }}
          >
            {vcamActive ? "ACTIVE" : (isLiteEdition() ? "OPEN" : "OFF")}
          </span>
        </button>

        {/* Picture-in-Picture Button */}
        {onTogglePiP && (
          <button
            onClick={onTogglePiP}
            title="Toggle Floating Picture-in-Picture window"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-1)",
              height: 28,
              padding: "0 var(--space-2)",
              borderRadius: "var(--radius-md)",
              border: `1px solid ${isPiPActive ? "var(--status-live)" : "var(--border-default)"}`,
              background: isPiPActive ? "var(--status-live-dim)" : "var(--bg-floating)",
              color: isPiPActive ? "var(--status-live)" : "var(--text-secondary)",
              fontSize: "var(--text-xs)",
              fontWeight: "var(--weight-semibold)",
              cursor: "pointer",
              transition: "all var(--duration-fast)",
            }}
          >
            <span>⧉ PiP</span>
          </button>
        )}

        {/* Virtual Microphone Button */}
        <button
          onClick={onToggleVMic}
          title="Virtual Microphone (Studio Workstation Feature)"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-1)",
            height: 28,
            padding: "0 var(--space-3)",
            borderRadius: "var(--radius-md)",
            border: `1px solid ${vmicActive ? "var(--status-live)" : "var(--border-default)"}`,
            background: vmicActive ? "var(--status-live-dim)" : "var(--bg-floating)",
            color: vmicActive ? "var(--status-live)" : "var(--text-secondary)",
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-semibold)",
            fontFamily: "var(--font-sans)",
            cursor: "pointer",
            transition: "all var(--duration-fast)",
          }}
        >
          <span>Virtual Mic</span>
          <span
            style={{
              fontSize: "9px",
              padding: "1px 5px",
              borderRadius: "var(--radius-full)",
              background: vmicActive ? "var(--status-live)" : "var(--bg-elevated)",
              color: vmicActive ? "#000" : "var(--text-muted)",
              fontWeight: "var(--weight-bold)",
              textTransform: "uppercase",
            }}
          >
            {vmicActive ? "ON" : "OFF"}
          </span>
        </button>

        <div style={{ flex: 1 }} />
        {isLiveKit && lkStatus !== "idle" && (
          <StatusBadge status={lkStatus} error={lkError} />
        )}
      </div>

      {/* Video row */}
      <div style={{
        flex: 1,
        display: "flex",
        gap: "var(--space-4)",
        overflow: "hidden",
        minHeight: 0,
      }}>
        {/* Input tile — only shown in WebSocket mode or when webcam is active */}
        {(!isLiveKit || hasStream) && (
          <VideoTile
            label="Input"
            sublabel={hasStream ? "Webcam" : "No source"}
            status={hasStream ? "live" : "idle"}
            processing={false}
          >
            <video
              ref={videoRef} autoPlay muted playsInline
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                display: hasStream ? "block" : "none",
                transform: "scaleX(-1)",
              }}
            />
            {!hasStream && <EmptyTile label="Start webcam to preview" />}
          </VideoTile>
        )}

        {/* Output tile */}
        <VideoTile
          label="Output"
          sublabel={outputLabel}
          status={swapping ? "live" : "idle"}
          processing={swapping}
        >
          {/* WebSocket mode: img element */}
          {!isLiveKit && (
            <img
              ref={outputRef}
              alt="output"
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                display: "block",
                visibility: swapping ? "visible" : "hidden",
              }}
            />
          )}

          {/* LiveKit mode: video element */}
          {isLiveKit && (
            <video
              ref={processedVideoRef}
              autoPlay playsInline muted
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                display: "block",
                visibility: swapping ? "visible" : "hidden",
                transform: "scaleX(-1)",
              }}
            />
          )}

          {!swapping && (
            <EmptyTile
              label={
                isLiveKit
                  ? (canSwap ? "Start Swap (WebRTC)" : "Upload face photo first")
                  : (canSwap ? "Start Swap to preview" : "Upload face + start webcam")
              }
            />
          )}
        </VideoTile>
      </div>

      {/* Controls toolbar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        background: "var(--bg-raised)",
        borderRadius: "var(--radius-xl)",
        border: "1px solid var(--border-subtle)",
        flexShrink: 0,
      }}>
        {!isLiveKit && (
          <>
            <ToolbarButton
              label={hasStream ? "Stop Webcam" : "Start Webcam"}
              icon={hasStream ? "⏹" : "▶"}
              variant={hasStream ? "danger" : "primary"}
              onClick={hasStream ? onStopWebcam : onStartWebcam}
            />
            <div style={{ width: 1, height: 20, background: "var(--border-subtle)" }} />
          </>
        )}
        <ToolbarButton
          label={swapping ? "Stop Swap" : `Start Swap (${isLiveKit ? "WebRTC" : "WS"})`}
          icon={swapping ? "⏸" : "⊕"}
          variant={swapping ? "danger" : "accent"}
          onClick={swapping ? onStopSwap : onStartSwap}
          disabled={!canSwap}
        />
        <div style={{ flex: 1 }} />
        {swapping && (
          <span style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-xs)",
            color: "var(--text-muted)",
          }}>
            {frames} frames · {fps}fps
          </span>
        )}
        {activeProfile && (
          <div style={{
            display: "flex", alignItems: "center", gap: "var(--space-2)",
            padding: "var(--space-1) var(--space-3)",
            borderRadius: "var(--radius-full)",
            background: "var(--accent-dim)",
            border: "1px solid var(--accent-glow)",
            fontSize: "var(--text-xs)",
            color: "var(--accent)",
            fontWeight: "var(--weight-semibold)",
          }}>
            ◈ {activeProfile.name}
          </div>
        )}
      </div>
    </div>
  );
}

function ModeButton({ label, active, onClick }: {
  label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: "var(--space-1)",
        height: 28, padding: "0 var(--space-3)",
        borderRadius: "var(--radius-full)",
        border: active ? "1px solid var(--accent)" : "1px solid var(--border-subtle)",
        background: active ? "var(--accent-dim)" : "transparent",
        color: active ? "var(--accent)" : "var(--text-muted)",
        fontSize: "var(--text-xs)",
        fontWeight: "var(--weight-semibold)",
        fontFamily: "var(--font-sans)",
        cursor: "pointer",
        transition: "all var(--duration-fast) var(--ease-out)",
      }}
    >
      {label}
    </button>
  );
}

function StatusBadge({ status, error }: { status: string; error: string | null }) {
  const info: Record<string, { color: string; text: string }> = {
    connecting: { color: "var(--status-warning)", text: "Connecting…" },
    waiting:    { color: "var(--status-warning)", text: "Waiting for GPU…" },
    active:     { color: "var(--status-live)",    text: "Connected" },
    error:      { color: "var(--status-error)",   text: error || "Error" },
  };
  const { color, text } = info[status] || { color: "var(--text-muted)", text: status };

  return (
    <span style={{
      display: "flex", alignItems: "center", gap: "var(--space-1)",
      fontSize: "var(--text-xs)", color,
      fontFamily: "var(--font-mono)",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "var(--radius-full)",
        background: color,
        animation: status === "active" ? "pulse-border 2s ease-in-out infinite" : "none",
      }} />
      {text}
    </span>
  );
}

function VideoTile({ label, sublabel, status, processing, children }: {
  label: string; sublabel: string;
  status: "live" | "idle"; processing: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexShrink: 0 }}>
        <div style={{
          width: 6, height: 6, borderRadius: "var(--radius-full)",
          background: status === "live" ? "var(--status-live)" : "var(--status-idle)",
          animation: status === "live" ? "pulse-border 2s ease-in-out infinite" : "none",
        }} />
        <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--text-primary)" }}>{label}</span>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{sublabel}</span>
      </div>
      <div style={{
        flex: 1, position: "relative", background: "var(--bg-raised)",
        borderRadius: "var(--radius-xl)", overflow: "hidden",
        border: `2px solid ${processing ? "var(--accent)" : "var(--border-subtle)"}`,
        transition: "border-color var(--duration-base)",
        boxShadow: processing ? "0 0 0 1px var(--accent-glow), 0 0 20px var(--accent-glow)" : "none",
        minHeight: 0,
      }}>
        {children}
        {processing && (
          <div style={{
            position: "absolute", bottom: 8, left: 8,
            padding: "2px var(--space-2)", borderRadius: "var(--radius-full)",
            background: "rgba(0,0,0,0.6)", fontSize: "var(--text-xs)",
            color: "var(--accent)", fontWeight: "var(--weight-semibold)",
            backdropFilter: "blur(4px)",
          }}>
            ⊕ StreamPersona
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyTile({ label }: { label: string }) {
  return (
    <div style={{
      position: "absolute", inset: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "var(--text-muted)", fontSize: "var(--text-sm)",
      textAlign: "center", padding: "var(--space-4)",
    }}>
      {label}
    </div>
  );
}

function ToolbarButton({ label, icon, variant, onClick, disabled = false }: {
  label: string; icon: string;
  variant: "primary" | "accent" | "danger" | "ghost";
  onClick: () => void; disabled?: boolean;
}) {
  const colors = {
    primary: { bg: "var(--bg-elevated)", color: "var(--text-primary)", hoverBg: "var(--bg-floating)" },
    accent:  { bg: "var(--accent)", color: "#000", hoverBg: "var(--accent-hover)" },
    danger:  { bg: "var(--status-error-dim)", color: "var(--status-error)", hoverBg: "rgba(239,68,68,0.25)" },
    ghost:   { bg: "transparent", color: "var(--text-secondary)", hoverBg: "var(--bg-elevated)" },
  }[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        display: "flex", alignItems: "center", gap: "var(--space-2)",
        height: 32, padding: "0 var(--space-3)",
        borderRadius: "var(--radius-md)", border: "none",
        background: colors.bg, color: colors.color,
        fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)",
        fontFamily: "var(--font-sans)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "all var(--duration-fast) var(--ease-out)",
      }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span>
      {label}
    </button>
  );
}
